/**
 * Shared ShipStation tag <-> email mapping used by both the backend userTag service
 * and the frontend order pickers. Keeping a single implementation avoids drift in the
 * email-normalization and override rules between client and server.
 */

export interface ShipstationTagLike {
  readonly tagId: number;
  readonly name: string;
}

/** Bidirectional map: tagId -> name and name -> tagId. */
export type TagDictionary = Record<string, string | number>;

/** Hand-maintained overrides for users whose email local-part does not match their tag name. */
export const TAG_EMAIL_OVERRIDES: Readonly<Record<string, string>> = {
  'lara.kosinski@redsystemventures.com': 'Luna',
  'abrahamprimera@kink.com': 'Abe Primera',
};

/** Build the bidirectional tag dictionary from a list of ShipStation tags. */
export function buildTagDictionary(allTags: readonly ShipstationTagLike[]): TagDictionary {
  const tagDictionary: TagDictionary = {};
  allTags.forEach((tag) => {
    tagDictionary[tag.tagId] = tag.name;
    tagDictionary[tag.name] = tag.tagId;
  });
  return tagDictionary;
}

/** Normalize an email local-part for tag matching: strip separators, lowercase. */
function normalizeEmailLocalPart(email: string): string {
  const localPart = email.split('@')[0];
  return localPart.replace(/[._-]/g, '').toLowerCase();
}

/** Resolve a user's ShipStation tag name from their email and the tag dictionary. */
export function getTagNameFromEmail(email: string, tagDictionary: TagDictionary): string | null {
  if (!email || !tagDictionary) return null;

  const normalizedEmailAddress = String(email).trim().toLowerCase();
  const overrideTagName = TAG_EMAIL_OVERRIDES[normalizedEmailAddress];
  if (overrideTagName && tagDictionary[overrideTagName]) {
    return overrideTagName;
  }

  const normalizedEmail = normalizeEmailLocalPart(email);

  for (const key of Object.keys(tagDictionary)) {
    if (isNaN(Number(key))) {
      const normalizedKey = key.replace(/\s+/g, '').toLowerCase();
      if (normalizedKey === normalizedEmail) {
        return key;
      }
    }
  }

  return null;
}
